import { InternalBet } from '@cgs/data-access';
import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { SceneCommon, SCENE_COMMON_TOKEN } from '@cgs/common';
import {
  BuyFeaturePopupComponent,
  DrawOrderConstants,
  GAME_ACTION_HANDLER_TOKEN,
  GameActionHandler,
  LAUNCH_PARAMS_STORE_TOKEN,
  LaunchParamsStore,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  SLOT_SESSION_TOKEN,
  SlotSession,
  SoundInstance,
} from '@cgs/machines';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '@cgs/provider-trifecta';
import { Button, Container, SceneObject, SoundSceneObject, TextSceneObject } from '@cgs/wge';

const DRAW_ORDER = DrawOrderConstants.Top + 1;

const enum SceneNames {
  BuyFeaturePopup = 'buyFeaturePopup',
  TotalBuy = 'count_total_buy',
  TotalBet = 'count_stake_buy',
  ButtonPlus = 'btn_plus',
  ButtonMinus = 'btn_minus',
  ButtonBuy = 'btn_buy',
  ButtonClose = 'btn_close',
  BackDropArea = 'buyFeatureAreaBtn',
  ClickSound = 'button_click',
  BuySound = 'button_shop',
}

const enum States {
  Show = 'show',
  Hide = 'hide',
  Empty = 'empty',
}

export class SantaBuyFeaturePopup extends BuyFeaturePopupComponent {
  private readonly container: Container;

  private popup: SceneObject | null = null;
  private totalBuy: TextSceneObject;
  private totalBet: TextSceneObject;

  private readonly slotSession: SlotSession;
  private readonly numberFormatter: INumberFormatter;
  private readonly launchParamsStore: LaunchParamsStore;
  private readonly trifectaLaunchParams: TrifectaLaunchParamsDto;
  private readonly gameActionHandler: GameActionHandler;

  private currentBetIndex: number;
  private readonly betValues: InternalBet[];

  private readonly currencyDecimals: number;
  private readonly currency: string;

  private readonly buyFeatureMultiplier: number;

  private readonly soundClick: SoundInstance;
  private readonly soundBuy: SoundInstance;

  constructor(container: Container) {
    super();
    this.container = container;

    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    const resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    const soundClickScene = resourcesComponent.sounds
      .findById<SoundSceneObject>(SceneNames.ClickSound)
      ?.findAllByType(SoundSceneObject)[0];

    const soundBuyInstance = resourcesComponent.sounds
      .findById<SoundSceneObject>(SceneNames.BuySound)
      ?.findAllByType(SoundSceneObject)[0];

    this.soundClick = new SoundInstance(soundClickScene || null);
    this.soundBuy = new SoundInstance(soundBuyInstance || null);

    this.currencyDecimals = launchParams.currencyDecimals;
    this.currency = launchParams.currencyId;
    this.buyFeatureMultiplier = launchParams.buyFeatureMultiplier;

    this.slotSession = container.resolve<SlotSession>(SLOT_SESSION_TOKEN);
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
    this.launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    this.trifectaLaunchParams = launchParams;
    this.gameActionHandler = container.resolve<GameActionHandler>(GAME_ACTION_HANDLER_TOKEN);

    this.betValues = this.slotSession.Bets;
  }

  private ensurePopup(): SceneObject {
    if (this.popup) {
      return this.popup;
    }

    const sceneCommon = this.container.resolve<SceneCommon>(SCENE_COMMON_TOKEN);
    const scene = sceneCommon.sceneFactory.build('buy_feature/scene');

    if (!scene) {
      throw new Error('Scene not found');
    }

    scene.initialize();
    scene.z = DRAW_ORDER;

    const resourceComponent = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    resourceComponent.root.addChild(scene);

    const popup = scene.findById<SceneObject>(SceneNames.BuyFeaturePopup);

    if (!popup) {
      throw new Error('Popup not found');
    }

    popup.initialize();

    const totalBuy = popup.findById<TextSceneObject>(SceneNames.TotalBuy);
    const totalBet = popup.findById<TextSceneObject>(SceneNames.TotalBet);
    const buttonPlus = popup.findById<Button>(SceneNames.ButtonPlus);
    const buttonMinus = popup.findById<Button>(SceneNames.ButtonMinus);
    const buttonBuy = popup.findById<Button>(SceneNames.ButtonBuy);
    const buttonClose = popup.findById<Button>(SceneNames.ButtonClose);
    const backdropArea = popup.findById<Button>(SceneNames.BackDropArea);

    if (!backdropArea) {
      throw new Error('Backdrop area not found');
    }

    if (!buttonClose) {
      throw new Error('Button close not found');
    }

    if (!totalBuy) {
      throw new Error('Total buy not found');
    }

    if (!totalBet) {
      throw new Error('Total bet not found');
    }

    if (!buttonPlus) {
      throw new Error('Button plus not found');
    }

    if (!buttonMinus) {
      throw new Error('Button minus not found');
    }

    if (!buttonBuy) {
      throw new Error('Button buy not found');
    }

    this.totalBuy = totalBuy;
    this.totalBet = totalBet;

    buttonPlus.clicked.listen(() => {
      this.playClickSound();
      this.handleBetChanged(1);
    });

    buttonMinus.clicked.listen(() => {
      this.playClickSound();
      this.handleBetChanged(-1);
    });

    buttonBuy.clicked.listen(() => {
      this.playClickSound();
      this.playBuySound();
      this.handleBuyFeature();
    });

    buttonClose.clicked.listen(() => {
      this.playClickSound();
      this.hide();
    });

    backdropArea.clicked.listen(() => {
      this.hide();
    });

    popup.stateMachine?.findById(States.Hide)?.enterAction.done.listen(() => {
      popup.stateMachine?.switchToState(States.Empty);
    });

    this.popup = popup;

    return popup;
  }

  public show(): void {
    const popup = this.ensurePopup();

    this.renderBet(this.slotSession.currentBet.bet);

    popup.stateMachine?.switchToState(States.Show);
    this.isShown.emit(true);
  }

  public hide(): void {
    this.popup?.stateMachine?.switchToState(States.Hide);
    this.currentBetIndex = -1;
    this.isShown.emit(false);
  }

  private renderBet(startBet: number): void {
    this.currentBetIndex = this.getCurrentBetIndex();
    const totalBet = startBet * this.slotSession.lines;
    const totalBuy = totalBet * this.buyFeatureMultiplier;

    this.totalBet.deinitialize();

    this.totalBuy.text = `${this.currency} ${this.numberFormatter.format(totalBuy, this.currencyDecimals)}`;
    this.totalBet.text = `${this.currency} ${this.numberFormatter.format(totalBet, this.currencyDecimals)}`;

    this.totalBet.initialize();
  }

  private handleBetChanged(direction: -1 | 1): void {
    if (!this.betValues[this.currentBetIndex + direction]) {
      return;
    }

    const newBet = this.betValues[this.currentBetIndex + direction].bet;
    this.currentBetIndex += direction;
    this.renderBet(newBet);
  }

  private handleBuyFeature(): void {
    const bet = this.launchParamsStore.state.bets[this.currentBetIndex].bet;
    const featurePrice =
      bet * this.launchParamsStore.state.maxLines * this.trifectaLaunchParams.buyFeatureMultiplier;

    this.gameActionHandler.buyFeature({ bet, featurePrice });

    this.hide();
  }

  private getCurrentBetIndex(): number {
    if (this.currentBetIndex !== undefined && this.currentBetIndex !== -1) {
      return this.currentBetIndex;
    }

    const currentBet = this.slotSession.currentBet;
    return this.betValues.findIndex((bet) => bet.bet === currentBet.bet);
  }

  private playClickSound(): void {
    this.soundClick.stop();
    this.soundClick.play();
  }

  private playBuySound(): void {
    this.soundBuy.stop();
    this.soundBuy.play();
  }
}
